import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronDown, FiClipboard } from 'react-icons/fi';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import usePermission from '@/hooks/usePermission';

// Mesmo padrão de linha do professor usado em Redação (EssayRow.jsx) e
// Atividades Complementares (ComplementaryActivityRowTeacher.jsx) — lista
// vertical em vez de grid de cards, com o mesmo shell (borda + shadow-card) e
// bloco de ações à direita (CTA + menu ⋯).
//
// Usado nas abas Grupos e Individuais, que compartilham o mesmo shape de dado
// (project_group com `projects: []`) e o mesmo card hoje (GroupProjectCard.jsx).
export function ProjectRowTeacher({ data, index = 0, onDelete }) {
  const navigate = useNavigate();

  const canEdit = usePermission('Editar projetos', 'Projetos');
  const canDelete = usePermission('Excluir projetos', 'Projetos');
  const showActionsMenu = canEdit || canDelete;

  const projects = data?.projects || [];
  const title = projects?.[0]?.name;

  // Mesmas condições de badge do card antigo (GroupProjectCard.jsx), inclusive
  // a checagem invertida de "pendente de avaliação" — comportamento
  // preservado de propósito, só a apresentação mudou.
  const hasOngoing = projects.some(p => p.approved === null);
  const hasPendingVersions = projects.some(pv => pv.approved === null || pv.approved === false);

  const meta = [{ icon: FiClipboard, text: `${projects.length} projetos` }];

  return (
    <div
      className="flex flex-wrap items-center gap-4 rounded-[16px] border border-neutral-100 bg-white p-4 shadow-card transition-shadow duration-150 hover:shadow-card-hover"
      style={{ animation: `trilha-in 380ms cubic-bezier(0.23, 1, 0.32, 1) ${250 + index * 70}ms both` }}
    >
      <div className="min-w-[200px] flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3
            className="mb-0 line-clamp-2 text-base font-extrabold leading-snug"
            style={{ color: '#222731' }}
            title={title}
          >
            {title}
          </h3>

          <Badge
            variant={hasOngoing ? 'info-soft' : 'success-soft'}
            className="shrink-0 rounded-full px-2.5 py-1 text-xs font-bold normal-case"
          >
            {hasOngoing ? 'Em andamento' : 'Aprovado'}
          </Badge>

          {!hasPendingVersions && (
            <Badge
              variant="amber-soft"
              className="shrink-0 rounded-full px-2.5 py-1 text-xs font-bold normal-case"
            >
              Pendente de avaliação
            </Badge>
          )}
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-semibold text-neutral-500">
          {meta.map((item, metaIndex) => (
            <span
              key={metaIndex}
              className="flex items-center gap-1.5 text-sm"
            >
              {item.icon && <item.icon className="h-4 w-4 text-mission" />}
              {item.text}
            </span>
          ))}
        </div>
      </div>

      <div className="flex w-full items-center gap-2 sm:w-auto sm:shrink-0">
        <PlayfulCardButton
          className="h-9 flex-1 px-6 sm:w-auto sm:flex-none"
          onClick={() => navigate(`/projetos/grupos/${data.id}`)}
        >
          Acessar
        </PlayfulCardButton>

        {showActionsMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <PlayfulCardButton
                variant="outline"
                aria-label="Opções"
                className="h-9 w-9 shrink-0 gap-0 p-0"
              >
                <FiChevronDown size={20} />
              </PlayfulCardButton>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
              {canEdit && <DropdownMenuItem onSelect={() => navigate(`/projetos/criar/passo/curso-turma/${data.id}`)}>Editar</DropdownMenuItem>}
              {canDelete && <DropdownMenuItem onSelect={onDelete}>Excluir</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
